'use client';

import React, { memo, useLayoutEffect, useRef } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  markdownRehypeRawSanitizePlugins,
  markdownRehypeSanitizePlugins,
} from '@/lib/markdown-rehype-plugins';
import { StyledList, StyledListItem } from './styledListMarkdown';
import {
  fullscreenZoomPreviewWrapperStyle,
  getFullscreenZoomScale,
  useDeferredFullscreenZoom,
} from '@/lib/fullscreen-zoom';
import { CITATION_SUP_CLASSNAME, CITATION_SUP_STYLE } from '@/shared/citationSupBadge';

interface AnswerStyleMarkdownProps {
  content: string;
  className?: string;
  /** Fullscreen MediaDisplay zoom steps; scales via CSS zoom. */
  zoomLevel?: number;
  isFullscreen?: boolean;
  /**
   * Allow inline HTML (e.g. citation ``text:`` highlights via ``<mark>``).
   * Always sanitized with rehype-sanitize.
   */
  allowRawHtml?: boolean;
  /**
   * After render, scroll to the first citation ``text:`` highlight or
   * inline citation image / video / audio embed.
   */
  scrollToFirstCitationTarget?: boolean;
}

/** First mark / media inserted by citation markdown enhancement. */
const CITATION_TARGET_SELECTOR = '[data-citation-target="true"]';

/** Stable plugin arrays — new array literals each render make react-markdown remount. */
const REMARK_PLUGINS = [remarkGfm];

const SCROLL_MARGIN_PX = 8;

/**
 * Scroll ``target`` into view inside ``container`` only — never ancestor scrollers
 * (chat pane / answer row). ``scrollIntoView`` would move those as well.
 *
 * ``getBoundingClientRect`` is in visual pixels (includes CSS ``zoom`` / transform
 * scale on this container); ``scrollTop`` is in layout coordinates — divide by the
 * effective visual scale so zoomed citation markdown lands on the right target.
 */
function scrollChildIntoContainer(container: HTMLElement, target: HTMLElement): void {
  const cRect = container.getBoundingClientRect();
  const tRect = target.getBoundingClientRect();
  const layoutWidth = container.offsetWidth || container.clientWidth;
  const visualScale = layoutWidth > 0 ? cRect.width / layoutWidth : 1;
  const deltaVisual = tRect.top - cRect.top - SCROLL_MARGIN_PX;
  const deltaLayout = visualScale > 0 ? deltaVisual / visualScale : deltaVisual;
  const nextTop = container.scrollTop + deltaLayout;
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
  container.scrollTop = Math.min(maxTop, Math.max(0, nextTop));
}

function isMediaCitationTarget(el: HTMLElement): boolean {
  const tag = el.tagName;
  return tag === 'IMG' || tag === 'VIDEO' || tag === 'AUDIO';
}

function isMediaIntrinsicReady(el: HTMLElement): boolean {
  if (el instanceof HTMLImageElement) {
    return el.complete && el.naturalHeight > 0;
  }
  if (el instanceof HTMLVideoElement || el instanceof HTMLAudioElement) {
    // HAVE_METADATA or better — layout size is known.
    return el.readyState >= HTMLMediaElement.HAVE_METADATA;
  }
  return true;
}

/**
 * Module-level component map so parent re-renders (e.g. MediaDisplay ResizeObserver)
 * do not remount citation ``<img>`` / ``<video>`` / ``<audio>`` embeds.
 */
const ANSWER_STYLE_MARKDOWN_COMPONENTS: Components = {
  h1: ({ children }) => (
    <h1 className="text-blue-400 font-medium text-lg lg:text-2xl border-b-[2px] border-solid border-blue-200 m-0 p-0 mt-1 mb-3 pb-0.5">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-blue-300 font-normal text-base lg:text-base m-0 p-0 pt-1 pb-2">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-blue-200 font-medium text-[15px] m-0 p-0 mt-3 mb-1 first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-blue-200/90 font-light text-sm m-0 p-0 pt-1 pb-1">{children}</h4>
  ),
  ul: ({ children, ...props }) => <StyledList {...props}>{children}</StyledList>,
  ol: ({ children, ...props }) => (
    <StyledList ordered {...props}>
      {children}
    </StyledList>
  ),
  li: ({ children, ...props }) => <StyledListItem {...props}>{children}</StyledListItem>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand-blue-light italic underline"
    >
      {children}
    </a>
  ),
  p: ({ children }) => {
    const isEmpty =
      !children ||
      (Array.isArray(children) &&
        children.every((child) => typeof child === 'string' && child.trim() === ''));
    if (isEmpty) return <p className="hidden" />;
    return (
      <p className="text-white font-light text-[14px] leading-snug m-0 pb-2 lg:max-w-[800px]">
        {children}
      </p>
    );
  },
  code: ({ className: codeClassName, children }) => {
    const isBlock = codeClassName?.startsWith('language-');
    if (isBlock) {
      return (
        <code
          className={`block bg-black/30 border border-white/10 rounded p-3 my-2 font-mono text-white/90 text-xs overflow-x-auto whitespace-pre ${codeClassName ?? ''}`}
        >
          {children}
        </code>
      );
    }
    return (
      <code className="bg-black/30 text-white/90 font-mono px-1.5 py-0.5 rounded text-xs">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-0">{children}</pre>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-blue-300/40 pl-4 my-2 text-white/70 italic font-light text-[14px]">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="my-2 w-full max-w-full overflow-x-auto custom-scrollbar2 pr-1">
      <table className="w-max min-w-full border-collapse text-sm text-white">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-white/10">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-white/20 px-3 py-2 text-left font-semibold leading-snug whitespace-nowrap">
      {children}
    </th>
  ),
  tr: ({ children }) => <tr className="even:bg-white/5">{children}</tr>,
  td: ({ children }) => (
    <td className="border border-white/20 px-3 py-2 align-top leading-relaxed wrap-break-word min-w-16">
      {children}
    </td>
  ),
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  em: ({ children }) => <em className="italic text-white/90">{children}</em>,
  // Same citation chip as answer-item / PresentationMarkdown.
  sup: ({ children }) => (
    <sup className={CITATION_SUP_CLASSNAME} style={CITATION_SUP_STYLE}>
      {children}
    </sup>
  ),
  mark: ({ children, className: markClassName }) => {
    const isCitationText = Boolean(
      markClassName && /\bcitation-source-text\b/.test(markClassName),
    );
    return (
      <mark
        className={`${markClassName ?? ''}${isCitationText ? ' scroll-mt-2' : ''}`}
        {...(isCitationText ? { 'data-citation-target': 'true' } : {})}
      >
        {children}
      </mark>
    );
  },
  img: ({ src, alt }) => {
    if (!src) return null;
    const kind =
      alt === 'citation video'
        ? 'video'
        : alt === 'citation audio'
          ? 'audio'
          : 'image';
    const isCitationMedia =
      alt === 'citation video' ||
      alt === 'citation audio' ||
      alt === 'citation image';
    const citationProps = isCitationMedia
      ? ({ 'data-citation-target': 'true' } as const)
      : {};
    const scrollMargin = isCitationMedia ? ' scroll-mt-2' : '';
    if (kind === 'video') {
      return (
        <video
          key={src}
          src={src}
          controls
          preload="metadata"
          className={`my-2 max-h-[420px] max-w-full rounded border border-white/20 bg-black/20${scrollMargin}`}
          aria-label={alt || 'citation video'}
          {...citationProps}
        />
      );
    }
    if (kind === 'audio') {
      return (
        <audio
          key={src}
          src={src}
          controls
          preload="metadata"
          className={`my-2 w-full max-w-full${scrollMargin}`}
          aria-label={alt || 'citation audio'}
          {...citationProps}
        />
      );
    }
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        key={src}
        src={src}
        alt={alt || 'citation image'}
        className={`my-2 max-h-[420px] max-w-full rounded border border-white/20 object-contain bg-black/20${scrollMargin}`}
        // Avoid lazy re-fetch / decode churn while the parent panel resizes.
        loading="eager"
        decoding="async"
        {...citationProps}
      />
    );
  },
};

/**
 * Dark-theme markdown matching the chat answer card (answer-item): blue headers,
 * light body text on transparent/dark backgrounds — not the white file MarkdownViewer.
 */
function AnswerStyleMarkdown({
  content,
  className,
  zoomLevel = 0,
  isFullscreen = false,
  allowRawHtml = false,
  scrollToFirstCitationTarget = false,
}: AnswerStyleMarkdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  /** Content+zoom key for which in-container scroll has fully settled (incl. media size). */
  const scrolledForKeyRef = useRef<string | null>(null);
  const deferredZoom = useDeferredFullscreenZoom(isFullscreen, zoomLevel);
  const scale = getFullscreenZoomScale(deferredZoom.layoutLevel);
  const citationScrollKey = `${content}::${scale}`;

  useLayoutEffect(() => {
    if (!scrollToFirstCitationTarget) return;
    if (scrolledForKeyRef.current === citationScrollKey) return;
    const root = containerRef.current;
    if (!root) return;

    const target = root.querySelector(CITATION_TARGET_SELECTOR);
    if (!(target instanceof HTMLElement)) return;

    let cancelled = false;
    let safetyTimeoutId: number | undefined;

    const finalize = () => {
      if (cancelled) return;
      scrollChildIntoContainer(root, target);
      scrolledForKeyRef.current = citationScrollKey;
    };

    const removeMediaListeners = () => {
      target.removeEventListener('load', onReady);
      target.removeEventListener('error', onReady);
      target.removeEventListener('loadedmetadata', onReady);
      if (safetyTimeoutId !== undefined) window.clearTimeout(safetyTimeoutId);
    };

    function onReady() {
      removeMediaListeners();
      finalize();
    }

    // Approximate position immediately (marks / placeholder media box).
    scrollChildIntoContainer(root, target);

    if (!isMediaCitationTarget(target) || isMediaIntrinsicReady(target)) {
      scrolledForKeyRef.current = citationScrollKey;
      return;
    }

    // Media-first citations: intrinsic size is unknown until load — scroll again once ready.
    target.addEventListener('load', onReady);
    target.addEventListener('error', onReady);
    target.addEventListener('loadedmetadata', onReady);
    // Fallback if media never fires (blocked / stalled).
    safetyTimeoutId = window.setTimeout(onReady, 2000);

    return () => {
      cancelled = true;
      removeMediaListeners();
    };
  }, [citationScrollKey, scrollToFirstCitationTarget]);

  return (
    <div
      ref={containerRef}
      className={`box-border h-full min-h-0 w-full overflow-auto custom-scrollbar2 bg-gray-800 px-3 py-2 text-white ${className ?? ''}`}
      style={{
        ...fullscreenZoomPreviewWrapperStyle(deferredZoom.cssScaleBoost),
        // Apply layout zoom for Evidence Preview (source card) as well as fullscreen.
        ...(scale !== 1 ? { zoom: scale } : {}),
      }}
      data-testid="answer-style-markdown"
    >
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={
          allowRawHtml ? markdownRehypeRawSanitizePlugins : markdownRehypeSanitizePlugins
        }
        components={ANSWER_STYLE_MARKDOWN_COMPONENTS}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default memo(AnswerStyleMarkdown);
